import React from "react"
import { observer } from "mobx-react-lite"

import MainLayout from "@components/layout/MainLayout/MainLayout"
import Logo from "@components/prototypes/Logo/Logo"
import Icon from "@components/ui/Icon/Icon"
import Text from "@components/ui/Typography/Text"
import { useAppConfig } from "@components/theme/AppConfigProvider"
import { IconName } from "@components/ui/Icon/IconName"
import {
  APP_BUILD_NUMBER,
  APP_RELEASE_DATE,
  APP_VERSION,
} from "@framework/constants/app-version"

import styles from "./AboutPage.module.sass"

interface DetailItem {
  icon: IconName
  label: string
  value: string
}

const details: DetailItem[] = [
  { icon: "rocket", label: "Version", value: APP_VERSION },
  { icon: "calendar-event-line", label: "Release Date", value: APP_RELEASE_DATE },
  { icon: "terminal-window-line", label: "Build Number", value: APP_BUILD_NUMBER },
]

const AboutPage: React.FC = observer(() => {
  const theme = useAppConfig()

  // DKSH ships its logo in brand red on the About page (the shared white
  // variant is used elsewhere). `primary` recolors the currentColor-based
  // SVG with the theme's primary color, which on DKSH is the brand red.
  const isDKSH = theme.copy.logo?.toUpperCase() === "DKSH"

  return (
    <MainLayout>
      <div className={styles.root}>
        <div className={styles.container}>
          <div className={styles.header}>
            <Logo
              name={theme.copy.logo}
              height={40}
              withText
              primary={isDKSH}
            />
          </div>

          <Text variant="h2" as="h2" className={styles.title}>
            About LabWizard
          </Text>

          <Text variant="body2" color="text70Color" className={styles.subtitle}>
            LabWizard is an intelligent product knowledge assistant designed to
            help users quickly access accurate, reliable, and in-depth product
            information. It streamlines the process of answering complex
            product-related questions, enabling teams to deliver consistent,
            customer-ready responses with confidence.
          </Text>

          <dl className={styles.details}>
            {details.map(({ icon, label, value }) => (
              <div className={styles.detailRow} key={label}>
                <span className={styles.detailIcon}>
                  <Icon name={icon} />
                </span>
                <dt className={styles.detailLabel}>
                  <Text variant="caption1" color="text70Color">
                    {label}
                  </Text>
                </dt>
                <dd className={styles.detailValue}>
                  <Text variant="h5" as="span">
                    {value}
                  </Text>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </MainLayout>
  )
})

export default AboutPage
